import { useState } from 'react';
import { ENDPOINTS, ENDPOINT_CATEGORIES } from '../../config/api';

interface EndpointListProps {
  selectedEndpoint: string | null;
  onSelect: (path: string) => void;
}

const SUGGESTED_QUERIES = [
  { label: "What are Ted's headline stats?", path: '/track-record' },
  { label: 'Is Ted a cultural fit?', path: '/cultural-fit' },
  { label: 'What is Ted seeking?', path: '/seeking' },
];

export default function EndpointList({ selectedEndpoint, onSelect }: EndpointListProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggleCategory = (category: string) => {
    setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  // Group endpoints by category
  const grouped: Record<string, Array<{ key: string; path: string; method: string; label: string }>> = {};
  for (const category of ENDPOINT_CATEGORIES) {
    grouped[category] = [];
  }
  for (const [key, ep] of Object.entries(ENDPOINTS)) {
    if (grouped[ep.category]) {
      grouped[ep.category].push({ key, path: ep.path, method: ep.method, label: ep.label });
    }
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Suggested queries */}
      <div className="p-3 border-b border-ti-border space-y-1.5">
        <p className="text-xs text-ti-text-dim uppercase tracking-wider font-semibold mb-2">
          Try asking
        </p>
        {SUGGESTED_QUERIES.map((q) => (
          <button
            key={q.path}
            onClick={() => onSelect(q.path)}
            className={`w-full text-left text-xs px-2.5 py-2 rounded-lg transition-colors cursor-pointer ${
              selectedEndpoint === q.path
                ? 'bg-ti-blue/15 text-ti-blue'
                : 'text-ti-text-muted hover:bg-ti-surface-hover hover:text-ti-text'
            }`}
          >
            &ldquo;{q.label}&rdquo;
          </button>
        ))}
      </div>

      {/* Endpoint categories */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {ENDPOINT_CATEGORIES.map((category) => {
          const items = grouped[category];
          if (!items || items.length === 0) return null;
          const isCollapsed = collapsed[category] ?? false;

          return (
            <div key={category} className="border-b border-ti-border last:border-b-0">
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-ti-text-dim uppercase tracking-wider hover:bg-ti-surface-hover transition-colors cursor-pointer"
              >
                <span>{category}</span>
                <svg
                  className={`w-3 h-3 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {!isCollapsed && (
                <div className="pb-1">
                  {items.map((ep) => (
                    <button
                      key={ep.key}
                      onClick={() => onSelect(ep.path)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors cursor-pointer ${
                        selectedEndpoint === ep.path
                          ? 'bg-ti-blue/10 text-ti-text border-l-2 border-ti-blue'
                          : 'text-ti-text-muted hover:bg-ti-surface-hover hover:text-ti-text border-l-2 border-transparent'
                      }`}
                    >
                      <span className="text-[10px] font-mono font-bold text-ti-green bg-ti-green/10 px-1.5 py-0.5 rounded">
                        {ep.method}
                      </span>
                      <span className="font-mono text-xs truncate">{ep.path}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
